import { useState } from "react";

import { SnoozeModal, useCan, usePermanentLocations, usePermanentRealtimeDealers } from "components";
import { useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { SnoozedLeadsFilters, SnoozedLeadsTable } from "modules/LeadsDashboard/Leads/SnoozedLeads/components";
import {
  useDeleteLeadsSnoozedSnoozeMutation,
  useLeadsSnoozedAppendSnoozeMutation,
  useLeadsSnoozedFilters,
  useLeadsSnoozedQuery
} from "modules/LeadsDashboard/Leads/SnoozedLeads/hooks";

import type { DeleteSnoozePayload } from "components";
import type { Snooze } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

export const SnoozedLeads = () => {
  const [snoozeModalData, setSnoozeModalData] = useState<AddInterventionPayload | QuestionResultData | null>(null);

  const { mutateAsync: appendSnooze, isPending: isAppendSnoozePending } = useLeadsSnoozedAppendSnoozeMutation();
  const { mutateAsync: deleteSnooze, isPending: isDeleteSnoozePending } = useDeleteLeadsSnoozedSnoozeMutation();
  const { data: snoozedLeads, refetch: refetchLeadsSnoozed, isFetching } = useLeadsSnoozedQuery();
  const { dealer_ids, location_ids } = useLeadsSnoozedFilters();

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);

  const snoozeAppointment = snoozeModalData && "appointment" in snoozeModalData ? snoozeModalData.appointment : undefined;
  const snoozeLocation = locations.find(location => location.id === snoozeAppointment?.dealer_location_id);
  const snoozeDealer = dealers.find(dealer => dealer.id === snoozeLocation?.dealer_id);

  useRegisterNavAction(() => (dealer_ids.length > 0 || location_ids.length > 0) && refetchLeadsSnoozed());

  const handleDeleteSnooze = async (payload: DeleteSnoozePayload) => {
    await deleteSnooze(payload);
  };

  const handleAppendSnooze = async (snooze: Snooze) => {
    if (!canCreateSnooze) return;
    await appendSnooze(snooze);
  };

  return (
    <>
      <SnoozedLeadsFilters />
      <SnoozedLeadsTable isFetching={isFetching} setModalData={setSnoozeModalData} snoozedLeads={snoozedLeads} />
      {snoozeModalData && (
        <SnoozeModal
          size="large"
          dealer={snoozeDealer}
          location={snoozeLocation}
          open={Boolean(snoozeModalData)}
          onClose={() => setSnoozeModalData(null)}
          closeOnDimmerClick
          data={snoozeModalData}
          onDeleteSnooze={handleDeleteSnooze}
          onAppendSnooze={handleAppendSnooze}
          isAppendSnoozeLoading={isAppendSnoozePending}
          isDeleteSnoozeLoading={isDeleteSnoozePending}
        />
      )}
    </>
  );
};
